import { Div, Element, reactive, Variable } from 'alkali'
import Renderer from 'tools/Renderer'
import * as _ from 'lodash'
import { pipe } from 'lodash/fp'

// we need this just to make registered with jquery
import * as Selectivity from 'selectivity'

function sanitize(value) {
	if (value != undefined) {
		const isPreformatted = value.id && value.text
		return isPreformatted ? value : { id: value, text: String(value) }
	} else return null
}

// wraps selectivity dropdown, adding support for fontawesome menu icons
class SelectivityDropdown extends Element('.selectivity-dropdown') {
	allowClear: boolean
	value: any
	placeholder: string
	private el: this
	private $: JQuery
	private config: any
	private items: Array<{ id: string; text: string } | string>
	private defaultValue: any
	private _value: Variable<any>

	private programaticEvent: boolean

	buildPlaceholder(el) {
		return cfg => {
			if (!cfg.searchInputPlaceholder) {
				cfg.searchInputPlaceholder = 'Search'
			}
			return cfg
		}
	}

	template(cfg) {
		const removeLink =
			'<a class="selectivity-single-selected-item-remove"><svg class="streamline-svg" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></a>'

		if (cfg.templates == null) {
			cfg.templates = {}
		}
		const icons = cfg.icons || {}
		const iconHtml = id => {
			const icon = icons[id]
			return icon ? `<i class='fa fa-${icon} fa-pull-left fa-fw'></i>` : ''
		}

		_.defaults(cfg.templates, {
			singleSelectInput: function(options) {
				return (
					'<div class="selectivity-single-select">' +
					'<input type="text" class="selectivity-single-select-input"' +
					(options.required ? ' required' : '') +
					'>' +
					'<div class="selectivity-single-result-container"></div>' +
					removeLink +
					'<svg class="streamline-svg selectivity-caret" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"></polyline></svg>' +
					'</div>'
				);
			},
			singleSelectedItem(options) {
				return (
					`<span class='selectivity-single-selected-item' data-item-id='${_.escape(
						options.id
					)}'` +
					(options.title ? ` title="${options.title}"` : '') +
					'>' +
					iconHtml(options.id) +
					_.escape(options.text) +
					'</span>'
				)
			},
			multipleSelectedItem(options) {
				const extraClass = options.highlighted ? ' highlighted' : ''
				const removeBtn = `<a class='selectivity-multiple-selected-item-remove'>
													<svg class="streamline-svg" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
												</a>`
				return `<span class='selectivity-multiple-selected-item ${extraClass}'
											data-item-id='${_.escape(options.id)}'>
									<span class='selectivity-multiple-selected-item-text'>${_.escape(options.text)}</span>
									${options.removable ? removeBtn : ''}
								</span>`
			},
			multipleSelectInput(options) {
				return `<div class="selectivity-multiple-input-container">
						${
							options.enabled
								? '<input type="text" autocomplete="off" autocorrect="off" autocapitalize="off" class="selectivity-multiple-input">'
								: '<span class="selectivity-multiple-input selectivity-placeholder"></span>'
						}
						<div class="selectivity-clearfix"></div>
						<svg class="streamline-svg selectivity-caret" viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"></polyline></svg>
						</div>`
			},
			resultItem(options) {
				return (
					'<div class="selectivity-result-item' +
					(options.children
						? ' selectivity-result-parent'
						: '') +
					(options.disabled ? ' disabled' : '') +
					'"' +
					(options.title ? ` title="${options.title}"` : '') +
					' data-item-id="' +
					_.escape(options.id) +
					'">' +
					iconHtml(options.id) +
					_.escape(options.menuText || options.text) +
					(options.additionalText ? '<span class="selectivity-result-additional">' + _.escape(options.additionalText) + '</span>' : '') +
					(options.submenu
						? '<svg class="streamline-svg" viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"></polyline></svg>'
						: '') +
					'</div>'
				)
			},
			resultLabel(options) {
				return (
					`<div class='selectivity-result-label'` +
					(options.title ? ` title="${options.title}"` : '') +
					'>' +
					_.escape(options.text) +
					'</div>'
				)
			},
		})
		return cfg
	}

	dropdown(el) {
		return cfg => {
			if (cfg.showSearchInputInDropdown !== false) {
				_.defaults(cfg.templates, {
					dropdown(options) {
						const dropdownHTML = Selectivity.Templates.dropdown(options)
						return dropdownHTML.replace(
							/<input /,
							'<span class="fa fa-search dropdown-search-icon"></span><input '
						)
					},
				})
			}
			return cfg
		}
	}

	positionDropdown(cfg) {
		cfg.positionDropdown = ($dropdownEl, $selectEl) => {
			$dropdownEl = $($dropdownEl)
			$selectEl = $($selectEl)
			if ($dropdownEl.parent()[0] !== document.body) {
				$dropdownEl.appendTo(document.body)
			}

			const position = $selectEl.offset()
			const dropdownHeight = $dropdownEl.height()
			const selectHeight = $selectEl.height()
			const top = $selectEl[0].getBoundingClientRect().top
			const bottom = top + selectHeight + dropdownHeight
			const openUpwards =
				typeof window !== 'undefined' && bottom > $(window).height() && top - dropdownHeight > 0

			const width = $selectEl.outerWidth ? $selectEl.outerWidth() : $selectEl.width()
			$dropdownEl.addClass('is-dropdown')
			$dropdownEl
				.css({
					left: position.left + 'px',
					top: position.top + (openUpwards ? -dropdownHeight : selectHeight) + 'px',
				})
				.width(width)
		}
		return cfg
	}

	updaters(el) {
		return cfg => {
			for (const key in cfg) {
				const value = cfg[key]
				// check for variables in any of the options
				if (value != null ? value.subscribe : undefined) {
					let resolved
					let isPromise = true
					value.then(value => {
						isPromise = false
						resolved = value
					}, console.error)
					cfg[key] = resolved
					if (key === 'value') {
						this._value = value
						this.value = this.mapValue(this._value, cfg)
					}
					new Renderer({
						variable: value,
						element: el,
						updateOnStart: isPromise, // Type checking: this._value is not used?
						renderUpdate: this.renderUpdate(el, key, cfg, this._value),
					})
				}
			}
			return cfg
		}
	}

	static sanitizeData(vs) {
		// to stay well typed, always return an array
		return vs && _.isArray(vs) ? vs.map(sanitize) : vs ? [sanitize(vs)] : []
	}

	renderUpdate(el, key, selectivityCfg, v: any) {
		return value => {
			let oldValue, optionsUpdate
			// this is to debounce the input, preventing a loop
			this.programaticEvent = true
			if (key === 'value') {
				const findItem = (options, item) => {
					for (let option of options || []) {
						if (option.id === item || option === item) {
							return typeof option === 'object'
								? option
								: {
										id: option,
										text: option,
								  }
						}
						if (option.children && (option = findItem(option.children, item))) {
							return option
						}
					}
				}
				if (selectivityCfg.multiple) {
					el.$.selectivity(
						'data',
						value
							? _.compact(
									value.map(item =>
										item !== undefined && typeof item !== 'object'
											? sanitize(findItem(selectivityCfg.items, item))
											: item
									)
							  )
							: []
					)
				} else {
					oldValue = el.$.selectivity('data')
					if ((oldValue && oldValue.id) !== value) {
						if (typeof value !== 'object' && value !== undefined) {
							value = findItem(selectivityCfg.items, value)
						}
						if (!value || value == null) el.$.selectivity('clear')
						else el.$.selectivity('data', value)
					}
				}
			} else {
				optionsUpdate = {}
				optionsUpdate[key] = value
				if (key === 'items') {
					this.setItems(value)
				} else {
					el.$.selectivity('setOptions', optionsUpdate)

					if (key === 'placeholder' && !this._value.valueOf()) {
						// you have to reset the data to get selectivity to update the placeholder
						el.$.selectivity('data', null)
					}
				}
			}
			// reset the flag to false so that
			// user interaction based can enter
			this.programaticEvent = false
		}
	}

	setItems(items) {
		this.selectivityOp('setOptions', { items })
		this.selectivityOp('rerenderSelection', true)
		this.config.items = items
		if (!this.config.multiple) {
			const currentVal = this._value.valueOf()
			if (currentVal) {
				let item = this.getItemForId(currentVal)
				if (!item || item.disabled || item.selectable === false) {
					// item configuration has updated and previous selection is no longer valid
					const prev = this.programaticEvent
					this.programaticEvent = false
					let value
					if (this.defaultValue) {
						// if we have a default, revert to it as long as it's still valid
						item = this.getItemForId(this.defaultValue)
						if (item && !item.disabled && item.selectable !== false) {
							value = this.defaultValue
						}
					}
					if (value) {
						this.selectivityOp('value', value)
					} else {
						// otherwise unset the selection
						this.selectivityOp('data', null)
					}
					this.programaticEvent = prev
				}
			}
		}
	}

	protected getItemForId(id) {
		return this.selectivityOp('getItemForId', id)
	}

	protected selectivityOp(op: string, ...args) {
		return this.$.selectivity(op, ...args)
	}

	elProps(properties, { id, readOnly, placeholder }) {
		if (placeholder) {
			$(this)
				.find('input')
				.attr('id', _.kebabCase(placeholder + '-input')) // for selenium
		}
		(properties?.classes?.disabled = readOnly || false)
		
	}

	handleChange(properties) {
		properties.onchange = e => {
			// don't put if we just put or if it is spurious change event from the internal text input box (in which case it is undefined)
			if (!this.programaticEvent && e.value !== undefined) {
				try {
					this._value.put(e.value)
				} catch (error) {
					console.warn('Attempting to assign value from dropdown', error)
				}
			}
		}
	}

	highlightSelected({ highlightSelected }) {
		if (highlightSelected) {
			const el$ = this.$
			this.addEventListener('selectivity-open', e => {
				const selected = $('body > .selectivity-dropdown').find(
					`[data-item-id="${el$.selectivity('value')}"]`
				)
				if (selected.length) {
					selected.css('font-weight', 'bold')
				}
			})
		}
	}

	buildCfg() {
		// this is a configuration processing pipeline
		// each step mutates cfg until it is finally
		// passed to $.selectivity
		return pipe(
			this.buildPlaceholder(this),
			this.template.bind(this),
			this.dropdown(this),
			this.positionDropdown,
			this.updaters(this)
		)
	}

	listeners(properties, cfg) {
		this.handleChange(properties)
		this.highlightSelected(cfg)
		let $backdrop = $('body > .selectivity-backdrop')
		if (!$backdrop.length) {
			$backdrop = $(document.body.append(Div('.selectivity-backdrop')))
			$backdrop.hide()
		}
		this.addEventListener('selectivity-opening', (event) => {
			if (properties.disabled)
				event.preventDefault()
		})
		this.addEventListener('selectivity-open', () => {
			$backdrop.one('click', () => this.selectivityOp('close'))
			if (cfg?.input?.input) {
				$('.selectivity-search-input').on(
					'input',
					_.debounce(({ target }) => cfg.input.input.put(target.value), 150)
				)
			}
			$backdrop.show()
		})
		this.addEventListener('selectivity-close', () => {
			setTimeout(() => {
				$backdrop.hide()
			}, 200)
		})
	}

	setSelection(value) {
		this.selectivityOp('value', value)
	}

	/*
	 additional properties
	 selectivityCfg : {
	 templates : {}?,
	 searchInputPlaceholder : String,
	 icons : [String],
	 showSearchInputInDropdown : Bool,
	 positionDropdown : (dropdownEl, selectEl) => (),
	 multiple : Bool,
	 id : String,
	 highlightSelected : Bool,
	 readOnly : Bool
	 }
	 */
	created(properties) {
		// internal component value
		const initValue = properties?.value
		this._value = reactive(initValue)
		// exposed component value
		this.value = this.mapValue(this._value, properties)
		const cfg = this.buildCfg()(properties)
		this.config = cfg
		this.el = this
		this.$ = $(this)
		// pass built cfg
		this.selectivityOp(cfg)
		delete properties.value
		this.elProps(properties, cfg)
		this.listeners(properties, cfg)
		if (properties.allowClear && !properties.readOnly) {
			(properties.classes || (properties.classes = {})).removable = this.value.to(v => !!v)
		}
	}

	protected mapValue(internalValue: Variable<any>, config): Variable<any> {
		return internalValue
	}

	hide() {
		return this.$.hide()
	}

	show() {
		return this.$.show()
	}
}
export default SelectivityDropdown.defineElement('selectivity-dropdown')
